import React, { FC, useMemo, useRef, useState } from 'react'

import axios from 'axios'
import clsx from 'clsx'
import fileDownload from 'js-file-download'
import { useClickAway } from 'react-use'

import { Icon } from 'src/components/atoms'
import { Menu, MenuItem } from 'src/components/atoms/dataDisplays/Menu'
import { SquareIconButton } from 'src/components/atoms/inputs/SquareIconButton'
import {
  CommonContentAssetDeleteModal,
  CommonContentAssetFileDeleteModal,
  CommonContentAssetMenuItem,
} from 'src/components/organisms'

import { useGetUsers, useToast, useAuthority } from 'src/hooks'
import { CommonContentAssetEntity } from 'src/types'

type Props = {
  asset: CommonContentAssetEntity
  onReUpload: () => void
  className?: string
  canEdit?: boolean
  canDelete?: boolean
}

export const CommonContentAssetMenu: FC<Props> = ({
  asset,
  onReUpload,
  className,
  canEdit,
  canDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false)
  const { canDeleteCommonContent } = useAuthority()

  const { users } = useGetUsers()

  const updatedBy = useMemo(() => {
    return users.find(user => user.id === asset.uploadedById)
  }, [users, asset.uploadedById])

  const hasMenuItems = useMemo(() => {
    return (
      asset.contentType !== 'empty' || // emptyでなければダウンロードが選択可能
      canDeleteCommonContent // 削除権限があれば削除が選択可能
    )
  }, [asset.contentType, canDeleteCommonContent])

  const { displayErrorTextToast } = useToast()

  const ref = useRef(null)
  useClickAway(ref, () => {
    if (showMenu) {
      setShowMenu(false)
    }
  })

  const handleOnLink = () => {
    window.open(asset.externalLink)
  }

  // NOTE: ファイルのダウンロード先が別ドメインのためaタグのdownload属性を使った方法が使用出来ないためjsで行う
  const downloadFile = async () => {
    try {
      // Why not: 別オリジンのCookie認可CDNのため、fetch/XHRはデフォルトでCookieを送らない
      const response = await axios.get(asset.url, { responseType: 'blob', withCredentials: true })
      fileDownload(response.data, asset.fileName ?? '')
    } catch {
      displayErrorTextToast('ファイルのダウンロードに失敗しました')
    }
  }

  return (
    <>
      <div className={className} ref={ref}>
        <div className="tw-w-[2.5rem]">
          {/* overflow回避策としてrelativeを指定している親要素に対してabsoluteを持たせる事で、Menu部分が共通コンテンツアセットカード外にも表示させる事が出来る */}
          <div className="tw-absolute">
            <div
              className={clsx(
                'tw-relative',
                // 選択出来る項目がない場合はメニュー自体非表示
                !hasMenuItems && 'tw-invisible'
              )}
            >
              <SquareIconButton
                size="s"
                type="button"
                onClick={() => setShowMenu(previous => !previous)}
              >
                <Icon size="--r1" path="more-vertical/soft/line" />
              </SquareIconButton>

              {showMenu && (
                <Menu className="tw-absolute tw-bottom-[-4px] tw-right-0 tw-translate-y-full tw-z-30">
                  {(asset.contentType === 'img' ||
                    asset.contentType === 'pdf' ||
                    asset.contentType === 'video') && (
                    <>
                      {canEdit && (
                        <MenuItem onClick={onReUpload}>
                          <CommonContentAssetMenuItem
                            iconPath="upload/soft/line"
                            text="再アップロード"
                          />
                        </MenuItem>
                      )}
                      <MenuItem onClick={downloadFile}>
                        <CommonContentAssetMenuItem
                          iconPath="download/soft/line"
                          text="ダウンロード"
                        />
                      </MenuItem>
                      {canDelete && (
                        <MenuItem onClick={() => setShowDeleteFileModal(true)}>
                          <CommonContentAssetMenuItem
                            iconPath="trash/soft/line"
                            text="アップロードファイルを削除"
                          />
                        </MenuItem>
                      )}
                    </>
                  )}
                  {asset.contentType === 'link' && (
                    <MenuItem onClick={handleOnLink}>
                      <CommonContentAssetMenuItem
                        iconPath="external-link/soft/line"
                        text="リンクを開く"
                      />
                    </MenuItem>
                  )}
                  {canDeleteCommonContent && (
                    <MenuItem onClick={() => setShowDeleteModal(true)}>
                      <CommonContentAssetMenuItem
                        iconPath="trash/soft/line"
                        text="ファイルを削除"
                        type="error"
                      />
                    </MenuItem>
                  )}
                </Menu>
              )}
            </div>
          </div>
        </div>
      </div>

      <CommonContentAssetFileDeleteModal
        asset={asset}
        updatedByEmail={updatedBy?.email ?? ''}
        isOpen={showDeleteFileModal}
        onClose={() => setShowDeleteFileModal(false)}
      />

      <CommonContentAssetDeleteModal
        asset={asset}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      />
    </>
  )
}
